import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { ref, computed } from '@vue/composition-api'
import { _getProcesses } from '@/@core/queries/process'
import i18n from '@/libs/i18n'
import { get, set, getMany, update } from 'idb-keyval';
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import { useRouter } from '@core/utils/utils'
import endpoints from '@/libs/endpoints'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default function useCommon() {
  const { role, worker_id, client, locations: userLocations } = store.state?.userStore?.userData
  const userId = worker_id != null ? worker_id.$oid : null
  const userLocationsQuery = userLocations?.map(e => ({ _id: e.value }))
  const { commitment_functionality, hide_physical_location_in_processes, supervisors_only_from_current_department } = JSON.parse(localStorage.getItem("clientData") || '{}')
  const { showErrorMessage } = useNotifications()
    const { updateImprovementWithKey, sendEmailImprovementsOrCommitments, createCommitmentEvent, uploadFileToS3 } = useCommonTodo()
  const { router } = useRouter()
  const { getItem, getItems, getItemsAggregate, updateItem, updateItems, createItem, getPresignedDownloadUrl } = useLambda()
  const { processesQueryWithOffset } = useMongoDBFunctions()
  const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')

  const mainQuery = {
    client_id: oid(client.$oid),
    deleted: { $ne: true }
  }

  const roles = ref([])
  const workersTotal = ref([])
  const workersTotalNotMapped = ref([])
  const workersSupervised = ref([])
  const workersSupervisedNotMapped = ref([])
  const workersSupervisedWithNoAssigneeOption = ref([])
  const supervisors = ref([])
  const supervisorsOnlyNames = ref([])
  const locations = ref([])
  const locationsWithPhysicalLocation = ref([])
  const physical_locations = ref([])
  const areas = ref([])
  const leads = ref([])
  const workerPrivileges = ref([])
  const privileges = ref([])
  const processes = ref([])
  const motives = ref([])
  const metadata = ref([])
  const metadataNotMapped = ref([])
  const metadataFilterOptions = ref({zone: [], agency: []})
  const metadataTypeSelect = ref([])
  const metadataTypeSelectNotMapped = ref({})
  const customFilterOptions = ref([])
  const feedbackPoints = ref([])
  const values = ref([])

  // Main IndexedDB/LocalStorage data keys that are essential for offline mode
  const storageDataKeys = [ "events", "locations", "processesComplete", "workersSupervised" ]

  const checkStorageData = () => {
    getMany(storageDataKeys)
      .then(values => {
        for (const value of values) {
          if (!value) {
            for (const key of storageDataKeys) {
              if (!localStorage[key]) {
                showErrorMessage(i18n.t('message.storage_data_not_detected'), false)
                break
              }
            }
            break
          }
        }
      })
  }

  const updateStorageData = () => {
    fetchAndStoreEvents()
    getRolesForDropDown()
    getWorkersTotalForDropDown("notMapped")
    getWorkersSupervisedForDropDown()
    getWorkersSupervisedForDropDown("notMapped")
    getSupervisorsForDropDown()
    getSupervisorsForDropDown("onlyNames")
    getLocationsForDropDown()
    getProcessesForDropDown()
    getMetadataForDropDown({ category: "improvement", option: "notMapped" })
    getMetadataForDropDown({ type: "select", option: "notMapped" })
    getMotivesForDropDown()
  }

  const storeDataToUpload = (storageProperty, payload) => {
    update(storageProperty, response => {
      if (response) {
        response.push({...payload})
        return response
      } else {
        return [{...payload}]
      }
    })
  }

  const handleError = ({error, defaultMessage, isStorageError}) => {
    if (isStorageError) return showErrorMessage(i18n.t('message.storage_data_fetch_error'))
    if (typeof error === 'string' && error.startsWith("timeout") || error?.message?.startsWith("timeout")) {
      return showErrorMessage(i18n.t('message.internet_connection_unstable'))
    }
    return showErrorMessage(defaultMessage)
  }

  const formatDatePicker = (date, hour = 12) => {
    let formattedDate;
    
    if (date instanceof Date || date.includes("T")) {
      // ISO 8601 format (e.g. 2025-01-17T15:00:00.000Z)
      formattedDate = new Date(date);
      formattedDate.setHours(hour, 0, 0, 0);
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // yyyy-mm-dd format
      const [year, month, day] = date.split("-").map(Number);
      formattedDate = new Date(year, month - 1, day, hour, 0);
    } else {
      // dd-mm-yyyy or dd-mm-yyyy hh:mm
      const [datePart, timePart] = date.split(" ");
      const [day, month, year] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart ? timePart.split(":").map(Number) : [hour, 0];
      formattedDate = new Date(year, month - 1, day, hours, minutes);
    }

    return formattedDate;
  }

  const formatIsoDate = (dateStr, includeTime = false) => {
    const date = new Date(dateStr)
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
    if (includeTime) {
      options.hour = '2-digit'
      options.minute = '2-digit'
    }
    const format = default_language === 'en' ? 'en-US' : 'es-AR'
    return date.toLocaleDateString(format, options)
  }

  const getRolesForDropDown = async () => {
    get('roles').then(response => {
      if (response) roles.value = response
      else if (localStorage.roles) roles.value = JSON.parse(localStorage.roles)
    })

    try {
      const items = await getItems({ collection: 'role', query: mainQuery, options: { projection : { name: 1 }, sort: { name: 1 } } })

      roles.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
      set('roles', roles.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('roles').then(response => {
        if (!response && !localStorage.roles) handleError({ isStorageError: true })
      })
    }
  }

  const getWorkersTotalForDropDown = async (option) => {
    if (option === "notMapped") {
      get('workersTotalNotMapped').then(response => {
        if (response) workersTotalNotMapped.value = response
        else if (localStorage.workersTotalNotMapped) workersTotalNotMapped.value = JSON.parse(localStorage.workersTotalNotMapped)
      })
    } else {
      get('workersTotal').then(response => {
        if (response) workersTotal.value = response
        else if (localStorage.workersTotal) workersTotal.value = JSON.parse(localStorage.workersTotal)
      })
    }

    try {
      const pipeline = [
        { $match: mainQuery },
        { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { name: 1, email: 1, supervisors: 1, locations: 1 } },
        { $sort: { name: 1 } },
        { $addFields: { _id: { $toString: "$_id" } } }
      ]

      const items = await getItemsAggregate({ collection: 'worker', pipeline })

      if (option === "notMapped") {
        workersTotalNotMapped.value = items || []
        set('workersTotalNotMapped', workersTotalNotMapped.value).then(() => {}).catch(error => console.log(error))
      } else {
        workersTotal.value = items?.map(({ name, _id }) => ({ title: name, value: _id })) || []
        set('workersTotal', workersTotal.value).then(() => {}).catch(error => console.log(error))
      }
    } catch (error) {
      console.log(error)
      if (option === "notMapped") {
        get('workersTotalNotMapped').then(response => {
          if (!response && !localStorage.workersTotalNotMapped) handleError({ isStorageError: true })
        })
      } else {
        get('workersTotal').then(response => {
          if (!response && !localStorage.workersTotal) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getWorkersSupervisedForDropDown = async (option, addId, includeNoAssigneeOption) => {
    if (option === "notMapped") {
      get('workersSupervisedNotMapped').then(response => {
        if (response) workersSupervisedNotMapped.value = response
        else if (localStorage.workersSupervisedNotMapped) workersSupervisedNotMapped.value = JSON.parse(localStorage.workersSupervisedNotMapped)
      })
    } else {
      get('workersSupervised').then(response => {
        if (response) workersSupervised.value = response
        else if (localStorage.workersSupervised) workersSupervised.value = JSON.parse(localStorage.workersSupervised)
      })
    }

    try {
      const reformattedQuery = { ...mainQuery }

      if (['supervisor', 'director'].includes(role)) {
        reformattedQuery.$or = [{ supervisors: oid(worker_id.$oid) }]

        // Add ID of the logged-in supervisor for improvements only
        if (addId === "addId") { 
          reformattedQuery.$or.push({ _id: oid(worker_id.$oid) } )
        }
      }

      const pipeline = [
        { $match: reformattedQuery },
        { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { name: 1, email: 1, supervisors: 1, locations: 1 } },
        { $sort: { name: 1 } },
        { $addFields: { _id: { $toString: "$_id" } } }
      ]

      const items = await getItemsAggregate({ collection: 'worker', pipeline })

      if (option === "notMapped") {
        workersSupervisedNotMapped.value = items || []
        set('workersSupervisedNotMapped', workersSupervisedNotMapped.value).then(() => {}).catch(error => console.log(error))
      } else {
        const workersMapped = items?.map(({ name, _id }) => ({ title: name, value: _id })) || []
        if (includeNoAssigneeOption) {
          workersSupervisedWithNoAssigneeOption.value = [
            { title: i18n.t('no_assignee'), value: 'no_assignee' },
            ...workersMapped
          ]
        } else {
          workersSupervised.value = workersMapped
        }
        set('workersSupervised', workersMapped).then(() => {}).catch(error => console.log(error))
      }
    } catch (error) {
      console.log(error)
      if (option === "notMapped") {
        get('workersSupervisedNotMapped').then(response => {
          if (!response && !localStorage.workersSupervisedNotMapped) handleError({ isStorageError: true })
        })
      } else {
        get('workersSupervised').then(response => {
          if (!response && !localStorage.workersSupervised) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getSupervisorsForDropDown = async (option, workerId) => {
    if (option === "onlyNames") {
      get('supervisorsOnlyNames').then(response => {
        if (response) supervisorsOnlyNames.value = response
        else if (localStorage.supervisorsOnlyNames) supervisorsOnlyNames.value = JSON.parse(localStorage.supervisorsOnlyNames)
      })
    } else {
      get('supervisors').then(response => {
        if (response) supervisors.value = response
        else if (localStorage.supervisors) supervisors.value = JSON.parse(localStorage.supervisors)
      })
    }

    try {
      const reformattedQuery = { ...mainQuery, $or: [{ privileges: 'supervisor' }, { privileges: 'director' }] }

      if (!['supervisor', 'director'].includes(role)) reformattedQuery.$or.push({ privileges: 'consultant' })
      
      // Exclude the worker that is being edited so that it can't be assigned as its own supervisor
      if (workerId) reformattedQuery._id = { $ne: oid(workerId) }
      
      const pipeline = [
        { $match: reformattedQuery },
        { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { name: 1, email: 1, supervisors: 1, locations: 1 } },
        { $sort: { name: 1 } },
        { $addFields: { _id: { $toString: "$_id" } } }
      ]

      const items = await getItemsAggregate({ collection: 'worker', pipeline })

      if (option === "onlyNames") {
        supervisorsOnlyNames.value = Array.from(new Set(items?.map(({ name }) => name) || []))
        set('supervisorsOnlyNames', supervisorsOnlyNames.value).then(() => {}).catch(error => console.log(error))
      } else {
        supervisors.value = items?.map(({ name, _id, locations }) => ({ title: name, value: _id, locationIds: locations.map(e => e._id), locationNames: locations.map(e => e.location) })) || []
        set('supervisors', supervisors.value).then(() => {}).catch(error => console.log(error))
      }
    } catch (error) {
      console.log(error)
      if (option === "onlyNames") {
        get('supervisorsOnlyNames').then(response => {
          if (!response && !localStorage.supervisorsOnlyNames) handleError({ isStorageError: true })
        })
      } else {
        get('supervisors').then(response => {
          if (!response && !localStorage.supervisors) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getLocationsForDropDown = async () => {
    get('locations').then(response => {
      if (response) locations.value = response
      else if (localStorage.locations) locations.value = JSON.parse(localStorage.locations)
    })

    try {
      const pipeline = [
        { $match: mainQuery },
        { $lookup: { from: 'physical_location', localField: 'physical_location', foreignField: '_id', as: 'physical_location', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { location: 1, physical_location: 1 } },
        { $sort: { location: 1 } },
        { $addFields: { _id: { $toString: "$_id" }, physical_location: { $arrayElemAt: ["$physical_location", 0] } } }
      ]

      const items = await getItemsAggregate({ collection: 'location', pipeline })

      locations.value = items?.map(({ location, _id, physical_location }) => ({ title: location, value: _id, physical_location: physical_location?._id })) || []
      locationsWithPhysicalLocation.value = items?.map(({ location, _id, physical_location }) => ({ title: `${location}${physical_location?.name ? `  - ${physical_location.name}` : ''}`, value: _id, physical_location: physical_location?._id })) || []
      set('locations', locations.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('locations').then(response => {
        if (!response && !localStorage.locations) handleError({ isStorageError: true })
      })
    }
  }

  const getPhysicalLocationsForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'physical_location', query: mainQuery })
      physical_locations.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getAreasForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'area', query: mainQuery })
      areas.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getValuesForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'value', query: mainQuery })
      values.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getFeedbackPointsForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'feedback_point', query: mainQuery })
      feedbackPoints.value = items?.map(({ name, _id, location, physical_location }) => ({ title: name, value: _id.toString(), location: location?.toString(), physical_location: physical_location?.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getdepartmentLeadsForDropDown = async () => {
    try {
      const reformattedQuery = { ...mainQuery, privileges: { $in: ['consultant', 'director', 'supervisor'] } }
      const items = await getItems({ collection: 'worker', query: reformattedQuery, options: { sort: { name: 1 } } })
      leads.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getProcessesForDropDown = async () => {
    get('processesDropdown').then(response => {
      if (response) processes.value = response
      else if (localStorage.processesDropdown) processes.value = JSON.parse(localStorage.processesDropdown)
    })

    try {
      const input = {
        limit: 10000,
        sortBy: 'name',
        client_id: client.$oid,
        worker_id: worker_id.$oid,
        role,
        locations: userLocations?.map(e => e.value),
      }

      const items = await processesQueryWithOffset(input)

      processes.value = items?.processes?.map(e => {
        e._id = e._id.toString()
        if (e.metadataData) {
          // This variable cannot come as 'metadata' from the backend because it interferes with the process schema validation
          e.metadata = e.metadataData
          delete e.metadataData
        }
        const physicalLocation = e.locationsData?.[0]?.physicalLocationName
        const shouldShowPhysicalLocation = !hide_physical_location_in_processes && physicalLocation
        return { title: shouldShowPhysicalLocation ? `${e.name} - ${physicalLocation}` : e.name, value: e._id }
      }) || []
      set('processesComplete', items?.processes || []).then(() => {}).catch(error => console.log(error))
      set('processesDropdown', processes.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('processesDropdown').then(response => {
        if (!response && !localStorage.processesDropdown) handleError({ isStorageError: true })
      })
    }
  }

  const getMotivesForDropDown = async () => {
    get('motives').then(response => {
      if (response) motives.value = response
      else if (localStorage.motives) motives.value = JSON.parse(localStorage.motives)
    })

    try {
      const items = await getItems({ collection: 'motive', query: mainQuery, options: { projection : { value: 1, label: 1 }, sort: { label: 1 } } })
      items?.forEach(e => e._id = e._id.toString())

      motives.value = items || []
      set('motives', motives.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('motives').then(response => {
        if (!response && !localStorage.motives) handleError({ isStorageError: true })
      })
    }
  }
  
  const getMetadataForDropDown = async ({category, option, type}) => {
    if (category === "filter") {
      get('metadataFilterOptions').then(response => {
        if (response) {
          response.forEach(e => {
            Object.assign(metadataFilterOptions.value, {[e.name]: e.options})
          })
        } else if (localStorage.metadataFilterOptions) {
          JSON.parse(localStorage.metadataFilterOptions).forEach(e => {
            Object.assign(metadataFilterOptions.value, {[e.name]: e.options})
          })
        }
      })
    }
    else if (option === "notMapped") {
      if (type === "select") {
        get('metadataTypeSelectNotMapped').then(response => {
          if (response) metadataTypeSelectNotMapped.value = response
          else if (localStorage.metadataTypeSelectNotMapped) metadataTypeSelectNotMapped.value = JSON.parse(localStorage.metadataTypeSelectNotMapped)
        })
      } else {
        get('metadataNotMapped').then(response => {
          if (response) metadataNotMapped.value = response
          else if (localStorage.metadataNotMapped) metadataNotMapped.value = JSON.parse(localStorage.metadataNotMapped)
        })
      }
    }
    else if (type === "select") {
      get('metadataTypeSelect').then(response => {
        if (response) metadataTypeSelect.value = response
        else if (localStorage.metadataTypeSelect) metadataTypeSelect.value = JSON.parse(localStorage.metadataTypeSelect)
      })
    }
    else {
      get('metadata').then(response => {
        if (response) metadata.value = response
        else if (localStorage.metadata) metadata.value = JSON.parse(localStorage.metadata)
      })
    }

    try {
      const reformattedQuery = { ...mainQuery }
      if (category) reformattedQuery.categories = category
      if (type) reformattedQuery.type = type

      const items = await getItems({ collection: 'metadata', query: reformattedQuery, options: { projection : { name: 1, type: 1, options: 1 }, sort: { name: 1 } } })
      items?.forEach(e => e._id = e._id.toString())

      if (category === "filter") {
        items?.forEach(e => {
          Object.assign(metadataFilterOptions.value, {[e.name]: e.options})
        })
        set('metadataFilterOptions', items).then(() => {}).catch(error => console.log(error))
      }
      else if (option === "notMapped") {
        if (type === "select") {
          items?.forEach(e => {
            Object.assign(metadataTypeSelectNotMapped.value, {[e.name]: e.options})
          })
          set('metadataTypeSelectNotMapped', metadataTypeSelectNotMapped.value).then(() => {}).catch(error => console.log(error))
        } else {
          metadataNotMapped.value = items || []
          set('metadataNotMapped', items).then(() => {}).catch(error => console.log(error))
        }
      }
      else {
        const metadataResponseMapped = computed(() => {
          return items?.map(({ name, _id }) => ({ title: i18n.t(`metadata.${name}`), value: _id }))
        })
        if (type === "select") {
          metadataTypeSelect.value = metadataResponseMapped
          set('metadataTypeSelect', metadataResponseMapped).then(() => {}).catch(error => console.log(error))
        }
        else {
          metadata.value = metadataResponseMapped
          set('metadata', metadataResponseMapped).then(() => {}).catch(error => console.log(error))
        }
      }
    } catch (error) {
      console.log(error)
      if (category === "filter") {
        get('metadataFilterOptions').then(response => {
          if (!response && !localStorage.metadataFilterOptions) handleError({ isStorageError: true })
        })
      }
      else if (option === "notMapped") {
        if (type === "select") {
          get('metadataTypeSelectNotMapped').then(response => {
            if (!response && !localStorage.metadataTypeSelectNotMapped) handleError({ isStorageError: true })
          })
        }
        get('metadataNotMapped').then(response => {
          if (!response && !localStorage.metadataNotMapped) handleError({ isStorageError: true })
        })
      }
      else if (type === "select") {
        get('metadataTypeSelect').then(response => {
          if (!response && !localStorage.metadataTypeSelect) handleError({ isStorageError: true })
        })
      }
      else {
        get('metadata').then(response => {
          if (!response && !localStorage.metadata) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getCustomFilterForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'custom_filter', query: mainQuery, options: { projection : { name: 1, locations: 1, process: 1, role: 1, supervisor: 1, worker: 1, origin: 1 }, sort: { name: 1 } } })
      
      items?.forEach(e => {
        e._id = e._id.toString()
        e.locations = e.locations?.map(l => l.toString()) || []
        e.process = e.process?.map(p => p.toString()) || []
        e.role = e.role?.map(r => r.toString()) || []
        e.supervisor = e.supervisor?.map(s => s.toString()) || []
        e.worker = e.worker?.map(w => w.toString()) || []
        e.origin = e.origin || ''
      })

      customFilterOptions.value = items || []
    } catch (error) {
      console.log(error)
    }
  }

  const fetchAndStoreEvents = async () => {
    const calendars = ["Pendientes", "Vencidas"]

    const now = new Date()
    const firstDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const firstDayOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)

    try {
      const initialQuery = {
        ...mainQuery,
        'extendedProps.calendar': { $in: calendars },
        start: { $gte: date(firstDayOfPreviousMonth), $lt: date(firstDayOfNextMonth) },
      }

      const finalQuery = {
        $or: [{ "attendee.deleted": { $ne: true } }, { attendee: { $exists: false } }]
      }

      if (['supervisor', 'director'].includes(role)) {
        finalQuery.$or[0]["attendee.supervisors"] = oid(worker_id.$oid)
        finalQuery.$or[1]["organizer.locations"] = { $in: userLocationsQuery.map(e => oid(e._id)) }
        finalQuery.$or[2] = { "attendee._id": worker_id.$oid }
      }

      const pipeline = [
        { $match: initialQuery },
        { $lookup: { from: 'worker', localField: 'attendee', foreignField: '_id', as: 'attendee', pipeline: [ { $project: { deleted: 1, supervisors: 1, name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'organizer', foreignField: '_id', as: 'organizer', pipeline: [ { $project: { locations: 1 } } ] } },
        { $addFields: { attendee: { $arrayElemAt: ["$attendee", 0] }, organizer: { $arrayElemAt: ["$organizer", 0] } } },
        { $match: finalQuery },
        { $lookup: { from: 'process', localField: 'process', foreignField: '_id', as: 'process', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'confirmation', localField: 'confirmation', foreignField: '_id', as: 'confirmation', pipeline: [ { $project: { pending: 1, supervisor: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', as: 'supervisor', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $addFields: { supervisor: { $arrayElemAt: ["$supervisor", 0] } } } ] } },
        { $lookup: { from: 'commitment', localField: 'commitment', foreignField: '_id', as: 'commitment', pipeline: [ { $project: { completed: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'improvement', localField: 'improvements', foreignField: '_id', as: 'improvements', pipeline: [ { $addFields: { _id: { $toString: "$_id" }, assistance: { $toString: "$assistance" } } }, { $lookup: { from: 'worker', localField: 'assignee', foreignField: '_id', as: 'assignee', pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $lookup: { from: 'worker', localField: 'subscribers', foreignField: '_id', as: 'subscribers', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $addFields: { assignee: { $arrayElemAt: ["$assignee", 0] } } } ] } },
        { $lookup: { from: 'worker', localField: 'participants', foreignField: '_id', as: 'participants', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $addFields: { _id: { $toString: "$_id" }, assistance: { $toString: "$assistance" }, process: { $arrayElemAt: ["$process", 0] }, confirmation: { $arrayElemAt: ["$confirmation", 0] }, commitment: { $arrayElemAt: ["$commitment", 0] } } },
      ]

      const items = await getItemsAggregate({ collection: 'event', pipeline })

      const events = items || []
      set('events', events).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
    }
  }

  const updateConfirmationWithKey = async (confirmationId, key) => {
    try {
      const query = { _id: oid(confirmationId) }
      const action = { $set: { imageKey: key } }

      await updateItem({ collection: 'confirmation', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_update_error'))
    }
  }

  const createImprovements = async (improvements, origin, originId, sendEmail = true) => {
    if (!improvements?.length) return

    const improvementCreationPromises = improvements.map(i => {
      const newImprovement = {
        client_id: oid(i.client_id),
        assignee: i.assignee ? oid(i.assignee._id) : null,
        note: i.note,
        tags: i.tags,
        description: i.description,
        dueDate: i.dueDate ? date(new Date(`${i.dueDate.slice(0, 10)} 12:00:00`)) : null,
        important: i.important,
        created_by: oid(worker_id),
        creation_date: new Date()
      }
      if (origin) newImprovement.origin = origin
      if (originId) newImprovement.origin_id = oid(originId)
      if (i.metadata?.length) {
        newImprovement.metadata = i.metadata.map(m => ({
          name: m.name,
          type: m.type,
          answer: m.answer,
        }))
      }
      if (i.subscribers?.length) newImprovement.subscribers = i.subscribers.map(s => oid(s))

      return new Promise(async (resolve, reject) => {
        try {    
          const { insertedId } = await createItem({ collection: 'improvement', payload: newImprovement })
          const newImprovementId = insertedId.toString()
          i._id = newImprovementId
          i.isNew = true
          
          const { fileInfo, destinationFolder } = i.imgData
          if (fileInfo) {
            uploadFileToS3(fileInfo, destinationFolder)
              .then((key) => updateImprovementWithKey(newImprovementId, key))
              .catch((err) => console.log(err))
          }

          resolve(i)
        } catch (error) {
          console.log(error)
          showErrorMessage(commitment_functionality ? i18n.t('message.commitment_error') : i18n.t('message.improvement_error'))
          resolve(null)
        }
      })
    })

    const resolvedPromises = await Promise.all(improvementCreationPromises)

    const filteredImprovements = resolvedPromises.filter(e => !!e)

    // Send email to assignee and subscribers with the details of the improvements created
    if (filteredImprovements?.length && sendEmail) {
      sendEmailImprovementsOrCommitments(filteredImprovements, 'add')
    }

    return filteredImprovements.map(e => e._id)
  }

  const createCommitment = async (commitment) => {
    try {    
      const { insertedId } = await createItem({ collection: 'commitment', payload: commitment })
      return createCommitmentEvent(commitment, insertedId)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.commitment_error'))
    }
  }

  const getEventId = async (property, id) => {
    try {
      const query = { [property]: oid(id) }
      const item = await getItem({ collection: 'event', query })
      return item?._id?.toString()
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_update_error'))
    }
  }

  const updateCommitmentOfEvent = async (commitmentId, eventDate) => {
    const dueDate = eventDate instanceof Date ? eventDate : new Date(eventDate)

    try {
      const query = { _id: oid(commitmentId) }
      const action = { $set: { dueDate: date(dueDate) } }

      await updateItem({ collection: 'commitment', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.commitment_update_error'))
    }
  }

  const handleAssistance = (assistanceId, improvementId) => {
    const routeData = assistanceId
      ? router.resolve({ name: 'habit-assistance-view', params: { assistanceId } })
      : router.resolve({ name: 'habit-assistance-new', params: { improvementId } })
    window.open(routeData.href, '_blank')
  }

  const openImage = async (imageKey) => {
    let w = window.open("");
    w.document.write(`<h2>${i18n.t('message.loading_image')}</h2>`);
    try {
      const { url } = await getPresignedDownloadUrl({ key: imageKey })

      const response = await fetch(url)
      if (!response.ok) throw new Error('Error fetching file')

      const base64 = await response.text()

      let image = new Image();
      image.src = base64;

      w.document.body.innerHTML = "";
      w.document.write(image.outerHTML);
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.err_open_image'))
    }
  }

  const openImageQRCodes = async (imageKey) => {
    try {
      const { url } = await getPresignedDownloadUrl({ key: imageKey })
      window.open(url, '_blank')
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.err_open_image'))
    }
  }

  function getPrivilegesForWorker() {
    let defaultPrivilege = []
    if (['supervisor', 'director'].includes(role)) {
      defaultPrivilege = [{ value: 'employee', title: i18n.t('userRoles.Employee') }]
    } else if (role === 'consultant') {
      defaultPrivilege = [
        { value: 'consultant', title: i18n.t('userRoles.Consultant') },
        { value: 'director', title: i18n.t('userRoles.Director') },
        { value: 'supervisor', title: i18n.t('userRoles.Supervisor') },
        { value: 'employee', title: i18n.t('userRoles.Employee') },
      ]
    } else if (role === 'client') {
      defaultPrivilege = [
        { value: 'consultant', title: i18n.t('userRoles.Consultant') },
        { value: 'director', title: i18n.t('userRoles.Director') },
        { value: 'supervisor', title: i18n.t('userRoles.Supervisor') },
        { value: 'employee', title: i18n.t('userRoles.Employee') },
      ]
    } else {
      defaultPrivilege = [
        { value: 'admin', title: 'Admin' },
        { value: 'consultant', title: i18n.t('userRoles.Consultant') },
        { value: 'director', title: i18n.t('userRoles.Director') },
        { value: 'supervisor', title: i18n.t('userRoles.Supervisor') },
        { value: 'employee', title: i18n.t('userRoles.Employee') },
      ]
    }
    workerPrivileges.value = defaultPrivilege
  }

  function generateObjectId() {
    const timestamp = (new Date().getTime() / 1000 | 0).toString(16);
    const objectId = timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => {
      return (Math.random() * 16 | 0).toString(16);
    }).toLowerCase();
   
    return objectId;
  }

  // Open AWS documents
  const openDocument = async (key) => {
    if (!key) {
      showErrorMessage(i18n.t('message.document_not_available'))
      return
    }
    const url = `https://${endpoints.aws_bucket}.s3.amazonaws.com/${key}`
    window.open(url)
  }

  const getWorkersByRoles = async (roles, notEmployees = false) => {
    try {
      const conditions = {
        client_id: oid(client.$oid),
        deleted: { $ne: true },
        roles: { $in: roles.map(e => oid(e)) }
      }

      if (notEmployees) {
        conditions.privileges = { $ne: 'employee' }
      }

      const workers = await getItems({ collection: 'worker', query: conditions })

      return workers
    } catch (error) {
      showErrorMessage(i18n.t('message.worker_fetch_error'));
    }
  }

  const getWorkersByChildRoles = async (childRoles) => {
    try {
      const conditions = {
        _id: { $in: childRoles.map(e => oid(e)) }
      }

      const childRolesData = await getItems({ collection: 'role', query: conditions })
      if (!childRolesData?.length) return []

      const parentRolesSet = new Set()

      childRolesData.forEach(role => {
        role.parentRoles?.forEach(parent => {
          parentRolesSet.add(parent.toString())
        })
      })

      const parentRoles = Array.from(parentRolesSet)
      if (!parentRoles?.length) return []

      const parentWorkers = await getWorkersByRoles(parentRoles, true)

      return parentWorkers

    } catch (error) {
      showErrorMessage(i18n.t('message.worker_fetch_error'));
    }
  }

  const assignSupervisorsBasedOnRoles = async (childRoles, parentRoles = [], workerData = {}) => {
    // If the function is called from worker add/edit, it should come with workerData and include _id and locations (both properties as string)
    // If the function is called from role edit, it should come with parentRoles as an array of strings (same as childRoles)

    try {
      // Get child and parent workers
      const childWorkers = workerData._id ? [workerData] : await getWorkersByRoles(childRoles)
      if (!childWorkers?.length) return

      const parentWorkers = parentRoles.length ? await getWorkersByRoles(parentRoles, true) : await getWorkersByChildRoles(childRoles)
      if (!parentWorkers?.length) return

      // Get child roles data to see if the location filter applies or not
      const childRolesData = await getItems({ collection: 'role', query: { _id: { $in: childRoles.map(e => oid(e)) } } })
      const excludeLocationFilterLogic = childRolesData?.some(role => role.excludeLocationFilterInSupervisorAssignation)

      // Create supervisors index by location
      const locationMap = new Map() // key: locationId (string), value: array of supervisors

      if (!excludeLocationFilterLogic) {
        for (const supervisor of parentWorkers) {
          const locations = supervisor.locations || []
          for (const loc of locations) {
            const locStr = loc.toString()
            if (!locationMap.has(locStr)) {
              locationMap.set(locStr, [])
            }
            locationMap.get(locStr).push(supervisor)
          }
        }
      }

      // Assign supervisors to child workers based on their locations
      for (const worker of childWorkers) {
        let supervisorIds

        if (excludeLocationFilterLogic) {
          supervisorIds = parentWorkers.filter(s => s._id.toString() !== worker._id.toString()).map(s => s._id)
        } else {
          const workerLocations = worker.locations || []
          const matchedSupervisorSet = new Set()
  
          for (const loc of workerLocations) {
            const locStr = loc.toString()
            const supervisorsForLoc = locationMap.get(locStr) || []
            supervisorsForLoc.forEach(s => matchedSupervisorSet.add(s._id.toString()))
          }
  
          if (matchedSupervisorSet.size === 0) continue
  
          supervisorIds = Array.from(matchedSupervisorSet).filter(id => id !== worker._id.toString())
        }

        const query = { _id: worker._id }
        const action = { $addToSet: { supervisors: { $each: supervisorIds.map(id => oid(id)) } } }

        updateItem({ collection: 'worker', query, action })
      }
    } catch (error) {
      showErrorMessage(i18n.t('message.err_assigning_supervisors'))
    }
  }

  const getWorkerIdsByDepartments = async (departments) => {
    try {
      const conditions = {
        client_id: oid(client.$oid),
        deleted: { $ne: true },
        locations: { $in: departments.map(id => oid(id)) },
      }

      const workers = await getItems({ collection: 'worker', query: conditions })

      return workers?.map(e => e._id) || []
    } catch (error) {
      showErrorMessage(i18n.t('message.worker_fetch_error'));
    }
  }

  const getDepartmentLeadIds = async (departments) => {
    try {
      const conditions = {
        _id: { $in: departments.map(id => oid(id)) }
      }

      const departmentsData = await getItems({ collection: 'location', query: conditions })
      if (!departmentsData?.length) return []

      let departmentLeadIds = []

      for (const department of departmentsData) {
        if (!department.departmentLeads?.length) continue

        departmentLeadIds = [ ...departmentLeadIds, ...department.departmentLeads]
      }

      return departmentLeadIds
    } catch (error) {
      showErrorMessage(i18n.t('message.location_fetch_error'));
    }
  }

  const getAreaLeads = async (departments) => {
    try {
      const pipeline = [
        { $match: { _id: { $in: departments.map(id => oid(id)) } } },
        { $lookup: { from: 'area', localField: 'area', foreignField: '_id', as: 'area', pipeline: [ { $project: { areaLeads: 1 } } ] } },
        { $project: { location: 1, area: 1 } },
        { $addFields: { _id: { $toString: "$_id" }, area: { $arrayElemAt: ["$area", 0] } } }
      ]

      const departmentsData = await getItemsAggregate({ collection: 'location', pipeline })
      
      if (!departmentsData?.length) return []

      let areaLeadIds = []

      for (const department of departmentsData) {
        if (!department.area?.areaLeads?.length) continue

        areaLeadIds = [ ...areaLeadIds, ...department.area.areaLeads]
      }

      return areaLeadIds
    } catch (error) {
      showErrorMessage(i18n.t('message.location_fetch_error'));
    }
  }

  const assignSupervisorsBasedOnDepartment = async (departments, departmentLeads = [], workerId = '') => {
    // If the function is called from worker add/edit, it should come with departments and workerId (both properties as string)
    // If the function is called from department edit, it should come with departments and departmentLeads (both properties as string)

    try {
      // Get child and parent workers
      let childWorkers = workerId ? [ workerId ] : await getWorkerIdsByDepartments(departments)
      if (!childWorkers?.length) return

      const parentWorkers = departmentLeads.length ? departmentLeads : await getDepartmentLeadIds(departments)
      if (!parentWorkers?.length && !supervisors_only_from_current_department) return

      // Check if a child worker is an area lead of a department, if so, exclude the parent worker assignment for that department
      const areaLeads = await getAreaLeads(departments)
      childWorkers = childWorkers.filter(cw => !areaLeads.some(al => al.toString() === cw.toString()))
      if (!childWorkers?.length) return

      // Assign supervisors to child workers based on their departments
      for (const childId of childWorkers) {
        const supervisorIds = parentWorkers.filter(parentId => parentId.toString() !== childId.toString())
        const query = { _id: oid(childId) }
        const action = supervisors_only_from_current_department
          ? { $set: { supervisors: supervisorIds.map(id => oid(id)) } }
          : { $addToSet: { supervisors: { $each: supervisorIds.map(id => oid(id)) } } }

        updateItem({ collection: 'worker', query, action })
      }
    } catch (error) {
      showErrorMessage(i18n.t('message.err_assigning_supervisors'))
    }
  }

  const toMinutes = (time) => {
    const [h, m] = time.split(':').map(Number)
    return h * 60 + m
  }

  const calculateDuration = (startTime, endTime) => {
    let start = toMinutes(startTime)
    let end = toMinutes(endTime)

    // Si cruza medianoche
    if (end < start) {
      end += 24 * 60
    }

    const diff = end - start

    const hours = Math.floor(diff / 60)
    const minutes = diff % 60

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  return {
    getRolesForDropDown,
    roles,
    getWorkersTotalForDropDown,
    workersTotal,
    workersTotalNotMapped,
    getWorkersSupervisedForDropDown,
    workersSupervised,
    workersSupervisedNotMapped,
    workersSupervisedWithNoAssigneeOption,
    getSupervisorsForDropDown,
    supervisors,
    supervisorsOnlyNames,
    privileges,
    getProcessesForDropDown,
    processes,
    getLocationsForDropDown,
    locations,
    getPhysicalLocationsForDropDown,
    physical_locations,
    getAreasForDropDown,
    areas,
    getValuesForDropDown,
    values,
    getFeedbackPointsForDropDown,
    feedbackPoints,
    getPrivilegesForWorker,
    workerPrivileges,
    getMotivesForDropDown,
    motives,
    getMetadataForDropDown,
    metadata,
    metadataNotMapped,
    metadataFilterOptions,
    metadataTypeSelect,
    metadataTypeSelectNotMapped,
    handleError,
    checkStorageData,
    updateStorageData,
    fetchAndStoreEvents,
    storeDataToUpload,
    formatDatePicker,
    createCommitment,
    updateConfirmationWithKey,
    createImprovements,
    getEventId,
    updateCommitmentOfEvent,
    customFilterOptions,
    getCustomFilterForDropDown,
    handleAssistance,
    openImage,
    generateObjectId,
    openDocument,
    formatIsoDate,
    assignSupervisorsBasedOnRoles,
    getdepartmentLeadsForDropDown,
    leads,
    openImageQRCodes,
    calculateDuration,
    assignSupervisorsBasedOnDepartment,
    locationsWithPhysicalLocation,
  }
}